// Article "Baromètre Isère Mai 2026 — 3 tués dont 2 motards"
// Source officielle : Préfecture de l'Isère + ONISR (baromètre mensuel départemental, mai 2026)
// Angle pédagogique : ancrage territoire Nord Isère / Bourgoin-Jallieu pour parler aux apprenants locaux.
// PDF source : "05- Baro_mensuel_Dép_2026_1page_VIV-4.pdf" (Préfecture 38 + DDT)

import {
  AlertTriangle,
  Bike,
  Users,
  TrendingUp,
  ShieldCheck,
  Eye,
  Heart,
  Target,
  MapPin,
  Calendar,
  Quote,
  Lightbulb,
  Gauge,
  Brain,
} from 'lucide-react';

export const barometreIsereMai2026Article = {
  slug: 'barometre-securite-routiere-isere-mai-2026',
  weekVolume: 22,
  weekYear: 2026,
  monthLabel: 'Juin 2026',
  category: 'Actualité Sécurité Routière · Isère',
  shortTitle: 'Baromètre Isère Mai 2026 — 3 tués, dont 2 motards',
  title: 'Mai 2026 en Isère : 3 morts sur les routes, dont 2 motards',
  kicker: '🚨 Baromètre départemental — Préfecture de l\'Isère',
  subtitle:
    "Le baromètre mensuel de la sécurité routière en Isère pour mai 2026 confirme une tendance nationale alarmante : 3 personnes ont perdu la vie sur les routes du département en un seul mois — dont 2 motards. Avec un cumul de 20 tués sur les 5 premiers mois de l'année, le département reste très exposé. Décryptage local pour comprendre — et se protéger.",
  heroImage: '/images/articles/route-rurale-sinueuse.jpg?v=20260608',
  heroImageAlt:
    "Route rurale sinueuse du Nord Isère au printemps — illustration du baromètre de la sécurité routière de mai 2026 : départementales de campagne où se concentre une grande partie de la mortalité motocycliste",
  topRightKicker: { line1: 'Données officielles', line2: 'Isère · Mai 2026' },
  readTimeMin: 5,
  publishedAt: '2026-06-08',
  seoDescription:
    "Baromètre Sécurité Routière Isère mai 2026 : 3 tués sur les routes du département (+33 %), dont 2 motards. Analyse pédagogique locale Nord Isère / Bourgoin-Jallieu et leçons pour les apprenants moto.",
  seoKeywords:
    'baromètre sécurité routière Isère mai 2026, accidents Isère, mortalité motards Isère, statistiques Préfecture 38, prévention deux-roues motorisé Nord Isère, accidents Bourgoin-Jallieu, sécurité routière département 38',
  about:
    "Analyse du baromètre mensuel départemental de la sécurité routière en Isère (mai 2026) : 3 tués (+33 %), dont 2 motards. Lecture pédagogique locale pour les apprenants moto du Nord Isère.",
  source: {
    label: 'Préfecture de l\'Isère — Baromètre mensuel sécurité routière',
    url: 'https://www.isere.gouv.fr/',
    refs:
      "Baromètre mensuel départemental — mai 2026. Source : Préfecture de l'Isère & Observatoire National Interministériel de la Sécurité Routière (ONISR). Données provisoires.",
  },
  chapo: {
    kicker: 'Pourquoi ces chiffres te concernent — directement',
    body:
      "Tu prépares ton permis moto en <strong>Nord Isère</strong> ? Tu vas rouler sur les <strong>mêmes routes</strong> que celles où 3 personnes sont mortes en mai. Et sur les 5 premiers mois, <strong>20 personnes</strong> ont perdu la vie dans le département. Ce ne sont pas des chiffres abstraits : ce sont des collègues motards, des riverains, des familles. Comprendre ce qui s'est passé sur <em>ces</em> routes, c'est déjà commencer à <em>ne pas être le suivant</em>.",
    sideStat: {
      kicker: 'Le chiffre choc',
      big: '+33 %',
      body: "C'est la hausse de la mortalité routière en Isère en mai 2026 vs mai 2025. <strong>3 tués contre 2 morts</strong> sur le même mois — et <strong>2 motards parmi eux</strong>.",
      accent: '#cf2031',
    },
  },
  pullQuote: {
    text:
      "Chaque vie perdue sur nos routes est une tragédie. Le département reste très exposé — particulièrement les usagers vulnérables, motocyclistes en tête. La sensibilisation et la formation initiale restent nos meilleurs leviers de prévention.",
    source: "Préfecture de l'Isère — Délégation Sécurité Routière (extrait)",
  },
  sections: [
    {
      part: 'Partie 1 — Les chiffres officiels',
      title: 'Mai 2026 en Isère : 53 accidents, 3 tués, 80 blessés',
      intro:
        "Voici les <strong>chiffres officiels</strong> publiés par la Préfecture de l'Isère pour mai 2026. Tous proviennent de l'ONISR (Observatoire National Interministériel de la Sécurité Routière). Données provisoires.",
      cards: [
        {
          icon: Heart,
          accent: '#cf2031',
          kicker: 'Mortalité',
          title: '3 tués en mai 2026',
          body:
            "<strong>+1 mort vs mai 2025</strong> (qui en comptait 2 → +33 %). Sur les 5 premiers mois 2026 : <strong>20 tués au total</strong> en Isère, soit identique au cumul 2025 mais avec une saisonnalité plus défavorable.",
        },
        {
          icon: Bike,
          accent: '#cf2031',
          kicker: 'Motards',
          title: '2 motards parmi les 3 tués',
          body:
            "Sur les 3 morts de mai, <strong>2 étaient à moto</strong> (66 %). C'est la <strong>catégorie la plus durement frappée</strong> sur le mois. Confirme la tendance nationale d'avril 2026 (+52 % de motards tués).",
        },
        {
          icon: AlertTriangle,
          accent: '#ea580c',
          kicker: 'Accidents corporels',
          title: '53 accidents · 80 blessés',
          body:
            "<strong>+15 accidents</strong> (+6 %) et <strong>+29 blessés</strong> (+8 %) vs mai 2025. Cumul depuis janvier 2026 : <strong>278 accidents et 402 blessés</strong> — soit <strong>+17 % de blessés</strong> par rapport au cumul 2025.",
        },
        {
          icon: TrendingUp,
          accent: '#7c3aed',
          kicker: 'Tendance 5 mois',
          title: '+17 % de blessés vs 2025',
          body:
            "Sur janvier → mai 2026 : <strong>20 tués</strong> (stable), mais <strong>278 accidents</strong> (+9 %) et <strong>402 blessés</strong> (+17 %). La gravité globale augmente, et les usagers vulnérables paient le plus lourd tribut.",
        },
      ],
    },
    {
      part: 'Partie 2 — Lecture territoire',
      title: 'Pourquoi le Nord Isère est particulièrement exposé',
      intro:
        "L'Isère cumule plusieurs facteurs qui expliquent une accidentalité moto soutenue. Identifier ces facteurs aide à <strong>adapter sa conduite</strong> sur le terrain local.",
      cards: [
        {
          icon: MapPin,
          accent: '#0891b2',
          kicker: 'Géographie',
          title: 'Cols, vallées et virages techniques',
          body:
            "Le département mêle <strong>plaine du Nord Isère</strong> (Bourgoin, Villefontaine, La Tour-du-Pin), <strong>Vercors</strong>, <strong>Belledonne</strong>, <strong>Chartreuse</strong> et <strong>Oisans</strong>. Les motards y trouvent des routes mythiques (col du Mortier, Croix de Fer, lacets de Chamrousse) mais aussi des virages serrés à adhérence variable.",
        },
        {
          icon: Gauge,
          accent: '#cf2031',
          kicker: 'Vitesse',
          title: 'Routes secondaires à 80 km/h',
          body:
            "Le département a maintenu le <strong>80 km/h</strong> sur la majorité des routes secondaires. La marge entre vitesse de référence et trajectoire de sécurité y est faible. Erreur de placement ou freinage tardif : la chute survient en quelques mètres.",
        },
        {
          icon: Calendar,
          accent: '#ea580c',
          kicker: 'Saisonnalité',
          title: 'Pic de mai à septembre',
          body:
            "<strong>Mai marque le retour massif</strong> des motos sur les routes après l'hiver. Beaucoup de pilotes n'ont pas roulé depuis 4-5 mois — les réflexes sont émoussés alors que la fréquentation grimpe. Le mois suivant (juin) est historiquement encore plus exposé.",
        },
      ],
    },
    {
      part: 'Partie 3 — Décryptage pédagogique',
      title: '3 leviers concrets pour TON apprentissage',
      intro:
        "Tu es en formation ou tu viens d'obtenir ton permis ? Voici <strong>3 leviers concrets</strong> qu'on travaille en cours et qui peuvent t'éviter de devenir une statistique du prochain baromètre.",
      cards: [
        {
          icon: Eye,
          accent: '#16a34a',
          kicker: 'Levier 1 — Anticipation',
          title: 'Regard projeté = sortie de virage',
          body:
            "Sur les <strong>routes sinueuses</strong> de l'Isère, regarder loin (vers la sortie du virage) suffit souvent à <strong>corriger spontanément</strong> la trajectoire. C'est la 1<sup>re</sup> compétence travaillée en formation moto, et celle qui sauve le plus souvent.",
        },
        {
          icon: ShieldCheck,
          accent: '#0891b2',
          kicker: 'Levier 2 — Distance de sécurité',
          title: '2 secondes minimum, 3 par sécurité',
          body:
            "Les accidents en intersection et carrefour représentent une part majeure des blessés en Isère. <strong>Gagner 1 seconde</strong> de distance, c'est gagner 22 mètres pour réagir à 80 km/h. À moto, c'est la différence entre la frayeur et l'hôpital.",
        },
        {
          icon: Brain,
          accent: '#7c3aed',
          kicker: 'Levier 3 — Auto-évaluation',
          title: 'Connais TES limites du jour',
          body:
            "Fatigue, stress, météo, pneus froids du matin… autant de variables qui modifient ta marge de sécurité. Les motards expérimentés <strong>roulent en dessous de leurs capacités max</strong>. À l'inverse, vouloir « se prouver » est l'erreur la plus coûteuse.",
        },
      ],
    },
    {
      part: 'Partie 4 — Cadre & sources',
      title: 'Où trouver les chiffres officiels',
      intro:
        "Le baromètre est <strong>public</strong> et publié chaque mois par la Préfecture de l'Isère. Tu peux le consulter librement.",
      cards: [
        {
          icon: Target,
          accent: '#1d4ed8',
          kicker: 'Méthodologie',
          title: 'Données ONISR + Préfecture 38',
          body:
            "Les chiffres sont consolidés par l'ONISR sur la base des procès-verbaux gendarmerie / police. Les données 2026 sont <strong>provisoires</strong> (consolidation finale T+12 mois). Moyennes 2023-2025 arrondies, évolutions calculées sur valeurs brutes.",
        },
        {
          icon: Quote,
          accent: '#7c3aed',
          kicker: 'Publication',
          title: 'Baromètre mensuel · 1 page',
          body:
            "Format : 1 page synthétique avec comparaison mois précédent + cumul annuel + moyenne 3 ans. Diffusé par la <strong>Délégation à la sécurité routière de l'Isère</strong> et accessible sur le site officiel de la Préfecture.",
        },
      ],
    },
  ],
  conclusion: {
    kicker: 'Conclusion — Ce que tu peux faire dès demain',
    body:
      "Ces chiffres ne sont pas une fatalité. <strong>20 morts en 5 mois</strong> en Isère, ce sont autant de familles brisées qui ne reverront pas leur proche. Si tu prépares ton permis moto, tu as une responsabilité : <strong>devenir un motard lucide</strong>, pas un statisticien anonyme. Cours en salle, plateau, circulation, magazine pédagogique : chaque module du Code 2 Roues est conçu pour t'éviter de figurer dans le prochain baromètre. <strong>Profite de ta formation</strong> — c'est le moment où on peut tout te transmettre sans que ça te coûte rien d'autre que ton attention.",
    cta: {
      label: '🎯 Reprendre la formation Plateau',
      to: '/formation',
      testid: 'cta-formation-from-barometre-isere',
    },
  },
  relatedSlugs: [
    'barometre-securite-routiere-avril-2026',
    'bien-choisir-casque-moto',
    'trajectoires-moto-securite',
    'distances-securite-moto',
  ],
};

export default barometreIsereMai2026Article;
